// Framework-neutral render functions (GUI §2 core components). Pure DOM, no state,
// no framework — C2 chooses the framework and may wrap or port these. Facts passed
// in (distance, severity, change-reason) are rendered verbatim: tone is a C-later
// concern and never alters these strings.

type El = HTMLElement;
function h(tag: string, cls?: string, text?: string): El {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (text != null) el.textContent = text;
  return el;
}

export function editorialStatusBlock(o: { microlabel: string; headline: string; subline?: string }): El {
  const root = h("section", "bx-status");
  root.append(h("div", "bx-microlabel", o.microlabel), h("h1", "bx-display", o.headline));
  if (o.subline) root.append(h("p", "bx-status__sub bx-body", o.subline));
  return root;
}

export function metricsStrip(cols: Array<{ value: string; label: string }>): El {
  const root = h("div", "bx-metrics");
  for (const c of cols.slice(0, 3)) {
    const m = h("div", "bx-metric");
    m.append(h("div", "bx-metric__value bx-display", c.value), h("div", "bx-microlabel", c.label));
    root.append(m);
  }
  return root;
}

export function severityTicks(n: number, max = 5): El {
  const root = h("div", "bx-ticks");
  for (let i = 0; i < max; i++) root.append(h("span", `bx-tick${i < n ? " bx-tick--on" : ""}`));
  return root;
}

export function button(o: { label: string; variant: "primary" | "secondary"; onClick?: () => void }): El {
  const b = h("button", `bx-btn bx-btn--${o.variant}`, o.label) as HTMLButtonElement;
  b.type = "button";
  if (o.onClick) b.addEventListener("click", o.onClick);
  return b;
}

/** Footer action row for multi-step sheets (report / ask-for-help). The advance button
 *  (Next / Submit) always sits bottom-right in the SAME spot across every step so it
 *  never jumps; an optional Back floats to the left. Pinned to the bottom of the sheet
 *  body via CSS so it's consistent regardless of how much a step renders above it. */
export function sheetActions(o: { back?: El; primary: El }): El {
  const row = h("div", "bx-sheet-actions");
  if (o.back) {
    o.back.classList.add("bx-sheet-actions__back");
    row.append(o.back);
  }
  row.append(o.primary);
  return row;
}

export function hairlineRow(o: { leading: string; trailing: string; tone?: "ink" | "aid" | "accent"; ugc?: string; icon?: SVGElement }): El {
  const row = h("div", `bx-row bx-hairline${o.tone && o.tone !== "ink" ? ` bx-row--${o.tone}` : ""}`);
  if (o.icon) {
    const slot = h("span", "bx-row__icon");
    slot.append(o.icon);
    row.append(slot);
  }
  row.append(h("span", "bx-row__lead bx-body", o.leading));
  if (o.ugc != null) row.append(h("span", "bx-row__ugc bx-ugc", o.ugc));
  row.append(h("span", "bx-row__trail bx-microlabel", o.trailing));
  return row;
}

export function bannerCard(o: { headline: string; detail: string }): El {
  const c = h("div", "bx-banner");
  c.append(h("div", "bx-display", o.headline), h("div", "bx-body", o.detail));
  return c;
}

export function pushToast(o: { title: string; body: string }): El {
  const t = h("div", "bx-toast");
  t.append(
    h("span", "bx-toast__glyph", "▲"),
    h("div", "bx-toast__title bx-body", o.title),
    h("div", "bx-toast__body bx-body", o.body),
  );
  return t;
}

export function bottomSheet(o: { title: string; content: El }): El {
  const sheet = h("div", "bx-sheet");
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", o.title);
  const close = h("button", "bx-sheet__close", "✕") as HTMLButtonElement;
  close.type = "button";
  close.setAttribute("aria-label", "Close");
  sheet.append(h("div", "bx-microlabel", o.title.toUpperCase()), close, o.content);
  return sheet;
}

/** Full-screen modal overlay (GUI §6) — the one DRY dialog surface used by every
 *  flow that needs to take over the frame (report, ask-for-help, change-home,
 *  flag/appeal/erase). An opaque paper sheet pinned over the app frame with a sticky
 *  header (title + ✕) and a scrolling body. Dismissed by the ✕ or Escape; the caller
 *  removes the node in `onClose`. Alert surfaces (toast-host, critical takeover) sit on
 *  higher z-index bands so notifications are never hidden behind a modal. */
export function modal(o: { title: string; content: El; onClose: () => void; sheetId?: string }): El {
  const root = h("div", "bx-modal");
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.setAttribute("aria-label", o.title);
  if (o.sheetId) root.setAttribute("data-sheet", o.sheetId);
  root.tabIndex = -1;
  const header = h("div", "bx-modal__header");
  header.append(h("div", "bx-microlabel bx-modal__title", o.title.toUpperCase()));
  const close = h("button", "bx-modal__close", "✕") as HTMLButtonElement;
  close.type = "button";
  close.setAttribute("aria-label", "Close");
  close.setAttribute("data-action", "modal-close");
  close.addEventListener("click", o.onClose);
  header.append(close);
  const body = h("div", "bx-modal__body");
  body.append(o.content);
  root.append(header, body);
  // Escape closes (stopPropagation so a nested modal doesn't also bubble to a parent).
  root.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Escape") { e.stopPropagation(); o.onClose(); }
  });
  return root;
}

const SVGNS = "http://www.w3.org/2000/svg";
function svgEl(tag: string, attrs: Record<string, string>): SVGElement {
  const e = document.createElementNS(SVGNS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
}

/** Hairline line-art glyphs for the primary action buttons (GUI §2 — structure over
 *  chrome). `report` = a hazard triangle; `ask-help` = a life-ring. Inherit currentColor
 *  so they read correctly on both the ink-filled FAB and a paper secondary button. */
function actionIcon(name: "report" | "ask-help" | "locate" | "frame"): SVGElement {
  const svg = svgEl("svg", {
    viewBox: "0 0 24 24", width: "26", height: "26", fill: "none", stroke: "currentColor",
    "stroke-width": "2", "stroke-linecap": "round", "stroke-linejoin": "round", "aria-hidden": "true",
  });
  if (name === "report") {
    svg.append(
      svgEl("path", { d: "M12 4 L21 19 H3 Z" }),
      svgEl("line", { x1: "12", y1: "10", x2: "12", y2: "14" }),
      svgEl("line", { x1: "12", y1: "17", x2: "12", y2: "17" }),
    );
  } else if (name === "locate") {
    svg.append(
      svgEl("circle", { cx: "12", cy: "12", r: "4" }),
      svgEl("line", { x1: "12", y1: "2", x2: "12", y2: "7" }),
      svgEl("line", { x1: "12", y1: "17", x2: "12", y2: "22" }),
      svgEl("line", { x1: "2", y1: "12", x2: "7", y2: "12" }),
      svgEl("line", { x1: "17", y1: "12", x2: "22", y2: "12" }),
    );
  } else if (name === "frame") {
    // Four corner brackets — "fit / frame all places".
    svg.append(
      svgEl("path", { d: "M4 8 V4 H8" }),
      svgEl("path", { d: "M16 4 H20 V8" }),
      svgEl("path", { d: "M20 16 V20 H16" }),
      svgEl("path", { d: "M8 20 H4 V16" }),
    );
  } else {
    svg.append(
      svgEl("circle", { cx: "12", cy: "12", r: "9" }),
      svgEl("circle", { cx: "12", cy: "12", r: "3.4" }),
      svgEl("line", { x1: "12", y1: "3", x2: "12", y2: "8.6" }),
      svgEl("line", { x1: "12", y1: "15.4", x2: "12", y2: "21" }),
      svgEl("line", { x1: "3", y1: "12", x2: "8.6", y2: "12" }),
      svgEl("line", { x1: "15.4", y1: "12", x2: "21", y2: "12" }),
    );
  }
  return svg;
}

/** Round icon-only action button. The label is carried on aria-label + title so the
 *  control stays named for assistive tech and tooltips even without visible text. */
export function iconButton(o: {
  label: string; icon: "report" | "ask-help" | "locate" | "frame"; variant: "primary" | "secondary"; onClick?: () => void;
}): El {
  const b = h("button", `bx-btn bx-btn--${o.variant} bx-icon-btn`) as HTMLButtonElement;
  b.type = "button";
  b.setAttribute("aria-label", o.label);
  b.title = o.label;
  b.append(actionIcon(o.icon));
  if (o.onClick) b.addEventListener("click", o.onClick);
  return b;
}

export function numberedSteps(steps: string[], current: number): El {
  const root = h("ol", "bx-steps");
  steps.forEach((s, i) => {
    const li = h("li", `bx-step${i === current ? " bx-step--current" : ""}`);
    li.append(h("span", "bx-step__n bx-microlabel", String(i + 1)), h("span", "bx-step__t bx-body", s));
    root.append(li);
  });
  return root;
}

/** A disclosure section, collapsed by default — the home for jargon-y/technical detail. */
export function collapsible(summary: string, body: El): El {
  const d = h("details", "bx-advanced") as HTMLDetailsElement;
  const s = h("summary", "bx-advanced__summary bx-microlabel", summary);
  d.append(s, body);
  return d;
}
